import React, { FC, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Button, useMediaQuery, useTheme } from '@mui/material';

import Dialog from 'components/dialog';
import Select from 'components/select';
import Switch from 'components/switch';
import FlexBox from 'components/flex-box';
import TextField from 'components/text-field';
import NumberField from 'components/number-field';
import LoadingSpinner from 'components/loading-spinner';
import RadioSelectItem from 'components/radio-select-item';

import { usePost } from 'hooks/use-post';
import { putFeeder } from '../api/put-feeder';

import { useAccountState } from 'contexts/account/account-context';
import { isValidDecimal, isValidInteger, isValidPositive } from 'utils/validate-numbers';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';

import { kgToLbs, lbsToKg } from 'views/self-service/utils/convertLbsKg';
import { Feeder } from 'views/self-service/features/feeders/types/feeders.type';
import { useFeedersState } from 'views/self-service/features/feeders/context/feeders-context';
import { UpdateFeederInput } from 'views/self-service/features/feeders/types/update-feeder-input.type';

type UpdateFeederProps = {
  idx: number;
  feeder: Feeder;
};

const MAX_STORAGE_CAPACITY = 500;
const MAX_SUPPORTED_FEEDING_BIOMASS = 15000;

const PAGE_NAME_ID = 'selfService.feeders';

const UpdateFeeder: FC<UpdateFeederProps> = ({ idx, feeder }) => {
  const intl = useIntl();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { account } = useAccountState();
  const { feederBrands } = useFeedersState();
  const snackbarDispatch = useSnackbarDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dialogRef = useRef<React.ElementRef<typeof Dialog>[]>([]);

  //#region STATE
  const [enableKG, setEnableKg] = useState<boolean>(false);
  //#endregion STATE

  // #region FORMS
  const { handleSubmit, control, getValues, setValue, reset } = useForm<UpdateFeederInput>({
    defaultValues: {
      id: 0,
      name: '',
      feederBrandId: '',
      storageCapacity: '',
      feedingCapacity: '',
      hidden: false,
    },
    shouldUnregister: true,
  });
  //#endregion FORMS

  // #region PUT DATA
  const updateFeeder = usePost<Feeder, { feederId: number; request: UpdateFeederInput }>(
    (request) => putFeeder(account.company!.companyId, request.feederId, request.request),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      setEnableKg(false);
      queryClient.invalidateQueries({
        queryKey: ['getFeeders']
      });
      dialogRef.current.forEach((dialog) => dialog.setOpen(false));
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );
  // #endregion PUT DATA

  // #region HANDLERS
  const handleUpdateFeeder = (request: UpdateFeederInput) => {
    updateFeeder.mutate({
      feederId: request.id,
      request: {
        ...request,
        storageCapacity: Number(request.storageCapacity),
        feedingCapacity: enableKG ? Number(request.feedingCapacity) : Math.round(lbsToKg(Number(request.feedingCapacity))),
      },
    });
  };

  const onKeyDown = (e: any) => e.stopPropagation();

  const calculateBiomass = () => {
    let biomass = getValues('feedingCapacity');
    let convertedBiomass = enableKG ? kgToLbs(Number(biomass)) : Math.round(lbsToKg(Number(biomass)));

    setValue('feedingCapacity', convertedBiomass === 0 ? '' : convertedBiomass);
  };

  // #endregion HANDLRES

  return (
    <Box display="flex" justifyContent={{ xs: 'center', md: 'flex-end' }} alignItems="center">
      <Dialog
        ref={(el) => {
          if (el) return (dialogRef.current[idx] = el);
        }}
        maxWidth="sm"
        onClose={() => setEnableKg(false)}
        title={intl.formatMessage({ id: `${PAGE_NAME_ID}.updateFeeder.dialog.title` })}
        button={
          <Button
            onClick={() =>
              reset({
                id: feeder.id,
                name: feeder.name,
                feederBrandId: feeder.feederBrandId,
                storageCapacity: feeder.storageCapacity,
                feedingCapacity: kgToLbs(feeder.feedingCapacity),
                hidden: feeder.hidden,
              })
            }
            size="small"
            sx={{ width: { xs: '100%' }, marginTop: { xs: '5%' } }}>
            {intl.formatMessage({ id: `${PAGE_NAME_ID}.list.edit` })}
          </Button>
        }>
        <form onSubmit={handleSubmit(handleUpdateFeeder)}>
          {updateFeeder.isPending && <LoadingSpinner overlay />}
          <FlexBox>
            <TextField
              fullWidth
              sx={{ display: 'none' }}
              label="id"
              controller={{
                name: 'id',
                control: control,
              }}
            />
            <TextField
              dark
              fullWidth
              maxLength={50}
              controller={{
                name: 'name',
                control: control,
                rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
              }}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.updateFeeder.dialog.name` })}
            />
            <Select
              dark
              nested
              fullWidth
              stopPropagation
              controller={{
                name: 'feederBrandId',
                control: control,
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                  validate: {
                    required: (v) => (parseInt(v) > 0 ? true : intl.formatMessage({ id: 'form.rules.required' })),
                  },
                },
              }}
              noResults={{
                show: !feederBrands?.length,
                text: <FormattedMessage id={'general.noResults'} />,
              }}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.updateFeeder.dialog.brand` })}>
              {feederBrands?.map((brand) => (
                <RadioSelectItem key={brand.id} value={brand.id}>
                  {brand.name}
                </RadioSelectItem>
              ))}
            </Select>
            <NumberField
              dark
              fullWidth
              stopPropagation
              onKeyDown={onKeyDown}
              endAdornment={'kg'}
              controller={{
                name: 'storageCapacity',
                control: control,
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                  validate: {
                    positive: (value: number) =>
                      isValidPositive(value) ? true : intl.formatMessage({ id: 'form.rules.invalidValue' }),
                    between: (value: number) =>
                      isValidInteger(value) && Number(value) <= MAX_STORAGE_CAPACITY
                        ? true
                        : intl.formatMessage({ id: 'form.rules.between' }, { lower: 0, upper: MAX_STORAGE_CAPACITY }),
                  },
                },
              }}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createFeeder.dialog.storageCapacity` })}
            />
            <NumberField
              dark
              fullWidth
              onKeyDown={onKeyDown}
              endAdornment={intl.formatMessage(
                { id: `${PAGE_NAME_ID}.createFeeder.dialog.endAdornment` },
                { unit: enableKG ? 'kg' : 'lbs' }
              )}
              controller={{
                name: 'feedingCapacity',
                control: control,
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                  validate: {
                    positive: (value: number) =>
                      isValidDecimal(value) && Math.round(lbsToKg(value))
                        ? true
                        : intl.formatMessage({ id: 'form.rules.invalidValue' }),
                    between: (value: number) =>
                      Math.round(lbsToKg(value)) || (isValidInteger(value) && Number(value) <= MAX_SUPPORTED_FEEDING_BIOMASS)
                        ? true
                        : intl.formatMessage({ id: 'form.rules.between' }, { lower: 0, upper: MAX_SUPPORTED_FEEDING_BIOMASS }),
                  },
                },
              }}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createFeeder.dialog.supportedBiomass` })}
            />
            <Box display={'flex'} gap={4}>
              <Switch
                dark
                value={feeder.hidden}
                controller={{
                  name: 'hidden',
                  control: control,
                }}
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.updateFeeder.dialog.inactive` })}
              />
              <Switch
                dark
                value={enableKG}
                onChange={() => {
                  setEnableKg((prev) => !prev);
                  calculateBiomass();
                }}
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createFeeder.dialog.switchLabel` })}
              />
            </Box>
            <Box display={'flex'} justifyContent={'end'}>
              <Button type="submit" fullWidth={isMobile}>
                <FormattedMessage id={`${PAGE_NAME_ID}.updateFeeder.dialog.button`} />
              </Button>
            </Box>
          </FlexBox>
        </form>
      </Dialog>
    </Box>
  );
};

export default React.memo(UpdateFeeder);
